import React, { FC } from 'react'
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Drawer,
  IconButton,
  Divider,
  Collapse,
  useTheme,
  Button,
  Stack,
  Tooltip,
} from '@mui/material'
import {
  BarChart as BarChartIcon,
  FaceRetouchingNatural as FaceRetouchingNaturalIcon,
  Medication as MedicationIcon,
  Settings as SettingsIcon,
  AutoFixHigh as AutoFixHighIcon,
  SwapHoriz as SwapHorizIcon,
  Store as StoreIcon,
  ExpandLess,
  ExpandMore,
  Menu as MenuIcon,
  HelpOutline as HelpOutlineIcon,
  PrintOutlined as PrintOutlinedIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
  Home as HomeIcon,
  Analytics as AnalyticsIcon,
  ListAlt as ListAltIcon,
  PersonSearch as PersonSearchIcon,
  People as PeopleIcon,
} from '@mui/icons-material'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { LAYOUT_CONSTANTS } from '@/shared/constants/layout'
import { useMyPharmacyQuery } from '@/features/yakureki'
import {
  KAKERU_GUIDE_PDF_URL,
  kakeruGuidePath,
  kakeruGuideVariantOf,
} from '@/pages/guide/companySupport'
import { useNavigate, useLocation } from 'react-router-dom'

// 共通ボタンスタイル定数
// 畳んだときのアイコンボタン。展開時の SIDEBAR_BUTTON_STYLES と同じ 2 色を使う
const SIDEBAR_ICON_BUTTON_STYLES = {
  primary: {
    color: colorTokens.blue[600],
    '&:hover': {
      bgcolor: colorTokens.action.primaryBgLight,
    },
  },
  secondary: {
    color: colorTokens.grey[600],
    '&:hover': {
      bgcolor: colorTokens.grey[100],
      color: colorTokens.grey[800],
    },
  },
}

const SIDEBAR_BUTTON_STYLES = {
  primary: {
    justifyContent: 'flex-start',
    color: colorTokens.blue[600],
    borderColor: colorTokens.blue[600],
    borderWidth: 1.5,
    py: 1,
    '&:hover': {
      bgcolor: colorTokens.action.primaryBgLight,
      borderColor: colorTokens.blue[700],
      borderWidth: 1.5,
    },
  },
  secondary: {
    justifyContent: 'flex-start',
    color: colorTokens.grey[700],
    borderColor: colorTokens.grey[400],
    borderWidth: 1.5,
    py: 1,
    '&:hover': {
      bgcolor: colorTokens.grey[50],
      borderColor: colorTokens.grey[600],
      borderWidth: 1.5,
      color: colorTokens.grey[800],
    },
  },
}

// グループとアイテムの型定義
interface PortalItem {
  id: string
  title: string
  path: string
  icon: React.ReactElement
  description?: string
}

interface PortalGroup {
  id: string
  title: string
  icon: React.ReactElement
  items: PortalItem[]
}

// ポータルグループ設定
const portalGroups: PortalGroup[] = [
  {
    id: 'data-analytics',
    title: 'データ分析',
    icon: <AnalyticsIcon />,
    items: [
      {
        id: 'usage',
        title: '利用統計',
        path: '/portal/metrics/usage',
        icon: <BarChartIcon />,
        description:
          'SOAP作成件数や患者総数と作成された指導の要点の統計を確認できます',
      },
      {
        id: 'guidance',
        title: '指導分析',
        path: '/portal/metrics/guidance',
        icon: <PersonSearchIcon />,
        description: '薬剤師個人の指導品質を分析し、改善ポイントを明確化します',
      },
      {
        id: 'records',
        title: '指導実績一覧',
        path: '/portal/metrics/records',
        icon: <ListAltIcon />,
        description:
          '服薬指導の実施状況、加算取得状況、音声記録などの詳細データを確認できます',
      },
    ],
  },
  {
    id: 'patient-management',
    title: '患者管理',
    icon: <FaceRetouchingNaturalIcon />,
    items: [
      {
        id: 'patients',
        title: '患者管理',
        path: '/portal/patients',
        icon: <FaceRetouchingNaturalIcon />,
        description:
          '患者情報の確認、ネクストアクション管理、来局履歴の確認ができます',
      },
    ],
  },
  {
    id: 'soap-settings',
    title: 'SOAP作成設定',
    icon: <MedicationIcon />,
    items: [
      {
        id: 'soap-settings',
        title: 'SOAP基本設定',
        path: '/portal/soap-generation',
        icon: <SettingsIcon />,
        description:
          'SOAP生成に関する詳細設定を変更し、Kakeru君の動作をカスタマイズできます',
      },
      {
        id: 'soaps',
        title: 'SOAPテンプレート登録',
        path: '/portal/soaps',
        icon: <MedicationIcon />,
        description:
          '医薬品やそのグループごとのSOAPテンプレートを登録し、Kakeru君に学習させられます',
      },
      {
        id: 'format-insert-settings',
        title: 'フォーマット自動挿入設定',
        path: '/portal/format-insert-settings',
        icon: <AutoFixHighIcon />,
        description:
          'キーワードに基づいてSOAPに定型文を自動挿入するルールを管理できます',
      },
      {
        id: 'text-replacements',
        title: '文字列置換設定',
        path: '/portal/text-replacements',
        icon: <SwapHorizIcon />,
        description:
          'SOAP生成時に自動的に適用される文字列置換ルールを管理できます',
      },
    ],
  },
  {
    id: 'pharmacy-settings',
    title: '薬局設定',
    icon: <StoreIcon />,
    items: [
      {
        id: 'pharmacists',
        title: '薬剤師管理',
        path: '/portal/pharmacists',
        icon: <PeopleIcon />,
        description: '薬剤師の登録・編集・削除を行います',
      },
      {
        id: 'line-settings',
        title: 'LINE連携設定',
        path: '/portal/line-settings',
        icon: <SettingsIcon />,
        description:
          'LINE公式アカウントのチャネル情報を設定し、患者との連携を有効化します',
      },
    ],
  },
]

interface PortalSidebarProps {
  open: boolean
  onClose: () => void
  isMobile: boolean
  collapsed?: boolean
  onCollapseToggle?: () => void
}

export const PortalSidebar: FC<PortalSidebarProps> = ({
  open,
  onClose,
  isMobile,
  collapsed = false,
  onCollapseToggle,
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  // ［困ったときガイド］は latest と同じく法人の版へ。共通版のパスは直書きせず、
  // back が解決した版名から組む (アイセイは連絡先が本部サポートデスクで、共通版とは
  // 文言が違う)。どちらも別タブ。紙の PDF の実体は back が S3 から署名付き URL で
  // 返し、版も back が session から解決する。design-book ADR-0044・ADR-0051。
  const { data: pharmacy } = useMyPharmacyQuery()
  const guideUrl = kakeruGuidePath(
    kakeruGuideVariantOf(pharmacy?.guide_variant)
  )
  // 初期状態で全グループを展開
  const [expandedGroups, setExpandedGroups] = React.useState<string[]>(
    portalGroups.map((group) => group.id)
  )

  const handleGroupClick = (groupId: string) => {
    if (!collapsed) {
      const isExpanded = expandedGroups.includes(groupId)
      if (isExpanded) {
        setExpandedGroups(expandedGroups.filter((id) => id !== groupId))
      } else {
        setExpandedGroups([...expandedGroups, groupId])
      }
    }
  }

  const handleItemClick = (path: string) => {
    navigate(path)
    if (isMobile) {
      onClose()
    }
  }

  const sidebarContent = (
    <Box
      sx={{
        width: collapsed
          ? LAYOUT_CONSTANTS.sidebarCollapsedWidth
          : LAYOUT_CONSTANTS.sidebarWidth,
        height: '100%',
        bgcolor: colorTokens.background.paper,
        borderRight: `1px solid ${colorTokens.border.light}`,
        display: 'flex',
        flexDirection: 'column',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          p: collapsed ? 1.5 : 2,
          borderBottom: `1px solid ${colorTokens.border.light}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
        }}
      >
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DashboardIcon
              sx={{
                fontSize: 20,
                color: colorTokens.grey[600],
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: colorTokens.text.primary,
              }}
            >
              管理ポータル
            </Typography>
          </Box>
        )}
        {!isMobile && (
          <IconButton
            onClick={onCollapseToggle}
            size="small"
            sx={{
              ml: collapsed ? 0 : 1,
            }}
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        )}
        {isMobile && !collapsed && (
          <IconButton onClick={onClose} size="small">
            <MenuIcon />
          </IconButton>
        )}
      </Box>

      {/* グループリスト */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <List sx={{ p: 0, flex: 1, overflowY: 'auto' }}>
          {portalGroups.map((group, groupIndex) => (
            <React.Fragment key={group.id}>
              {/* グループヘッダー */}
              <ListItem disablePadding>
                <Tooltip title={collapsed ? group.title : ''} placement="right">
                  <ListItemButton
                    onClick={() => handleGroupClick(group.id)}
                    sx={{
                      py: 1.5,
                      px: collapsed ? 1 : 2,
                      justifyContent: collapsed ? 'center' : 'flex-start',
                      '&:hover': {
                        bgcolor: colorTokens.action.primaryBgLight,
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: collapsed ? 0 : 40,
                        color: colorTokens.grey[600],
                      }}
                    >
                      {React.cloneElement(group.icon, { fontSize: 'small' })}
                    </ListItemIcon>
                    {!collapsed && (
                      <>
                        <ListItemText
                          primary={group.title}
                          sx={{
                            '& .MuiListItemText-primary': {
                              color: colorTokens.text.primary,
                            },
                          }}
                        />
                        {expandedGroups.includes(group.id) ? (
                          <ExpandLess fontSize="small" />
                        ) : (
                          <ExpandMore fontSize="small" />
                        )}
                      </>
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>

              {/* グループアイテム */}
              <Collapse
                in={!collapsed && expandedGroups.includes(group.id)}
                timeout="auto"
                unmountOnExit
              >
                <List component="div" disablePadding>
                  {group.items.map((item) => {
                    const isActive = location.pathname === item.path
                    return (
                      <ListItem key={item.id} disablePadding>
                        <Tooltip
                          title={collapsed ? item.title : ''}
                          placement="right"
                        >
                          <ListItemButton
                            onClick={() => handleItemClick(item.path)}
                            sx={{
                              pl: collapsed ? 1 : 4,
                              py: 1,
                              justifyContent: collapsed
                                ? 'center'
                                : 'flex-start',
                              bgcolor: isActive
                                ? colorTokens.action.primaryBgLight
                                : 'transparent',
                              borderLeft: isActive
                                ? `3px solid ${colorTokens.blue[600]}`
                                : '3px solid transparent',
                              '&:hover': {
                                bgcolor: colorTokens.action.secondaryBgLight,
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: collapsed ? 0 : 32,
                                color: isActive
                                  ? colorTokens.blue[600]
                                  : colorTokens.text.secondary,
                              }}
                            >
                              {React.cloneElement(item.icon, {
                                fontSize: 'small',
                              })}
                            </ListItemIcon>
                            {!collapsed && (
                              <ListItemText
                                primary={item.title}
                                sx={{
                                  '& .MuiListItemText-primary': {
                                    color: isActive
                                      ? colorTokens.blue[600]
                                      : colorTokens.text.primary,
                                  },
                                }}
                              />
                            )}
                          </ListItemButton>
                        </Tooltip>
                      </ListItem>
                    )
                  })}
                </List>
              </Collapse>

              {/* グループ間の区切り線 */}
              {groupIndex < portalGroups.length - 1 && (
                <Divider sx={{ my: 1, mx: 2 }} />
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>

      {/* 下部のボタン */}
      <Box
        sx={{
          borderTop: `1px solid ${colorTokens.border.light}`,
          p: collapsed ? 1 : 2,
        }}
      >
        <Stack spacing={1}>
          {collapsed ? (
            <>
              <Tooltip title="メイン画面へ" placement="right">
                <IconButton
                  onClick={() => navigate('/')}
                  sx={SIDEBAR_ICON_BUTTON_STYLES.primary}
                >
                  <HomeIcon sx={{ fontSize: 26 }} />
                </IconButton>
              </Tooltip>
              <Divider sx={{ my: 0.5 }} />
              <Tooltip title="困ったときガイド" placement="right">
                <IconButton
                  href={guideUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={SIDEBAR_ICON_BUTTON_STYLES.secondary}
                >
                  <HelpOutlineIcon sx={{ fontSize: 26 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="印刷用PDF" placement="right">
                <IconButton
                  href={KAKERU_GUIDE_PDF_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={SIDEBAR_ICON_BUTTON_STYLES.secondary}
                >
                  <PrintOutlinedIcon sx={{ fontSize: 26 }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<HomeIcon />}
                onClick={() => navigate('/')}
                sx={SIDEBAR_BUTTON_STYLES.primary}
              >
                メイン画面へ戻る
              </Button>
              <Divider sx={{ my: 1 }} />
              <Button
                fullWidth
                variant="outlined"
                startIcon={<HelpOutlineIcon />}
                href={guideUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={SIDEBAR_BUTTON_STYLES.secondary}
              >
                困ったときガイド
              </Button>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<PrintOutlinedIcon />}
                href={KAKERU_GUIDE_PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={SIDEBAR_BUTTON_STYLES.secondary}
              >
                印刷用PDF
              </Button>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  )

  return (
    <>
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={open}
          onClose={onClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: collapsed
                ? LAYOUT_CONSTANTS.sidebarCollapsedWidth
                : LAYOUT_CONSTANTS.sidebarWidth,
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        <Box
          component="nav"
          sx={{
            width: collapsed
              ? LAYOUT_CONSTANTS.sidebarCollapsedWidth
              : LAYOUT_CONSTANTS.sidebarWidth,
            flexShrink: 0,
            position: 'fixed',
            height: '100vh',
            zIndex: theme.zIndex.drawer,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
        >
          {sidebarContent}
        </Box>
      )}
    </>
  )
}

export { portalGroups }
export type { PortalGroup, PortalItem }
